import React, { useState } from 'react';
import { Box, Flex, Icon, Portal, Text, VStack } from '@chakra-ui/react';
import { LuLock, LuUsers } from 'react-icons/lu';
import { EASE_OUT } from '@/components/ui/motion.ts';
import { processDroppedItems } from '@/utils/upload/uploadUtils.ts';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { useAuth } from '@/context/AuthContext.tsx';
import { Title } from '@/components/ui/title';

interface FileDropOverlayProps {
	/**
	 * Receives the dropped files and the privacy implied by which zone they landed on. An array
	 * rather than a `FileList`: rebuilding one through `DataTransfer` would discard the folder path
	 * the directory walk recorded on each file, which is what tells two same-named files apart.
	 */
	onDropFiles: (files: File[], isPrivate: boolean) => void;
}

/** The two halves: drop side encodes the record's privacy. */
const ZONES = [
	{
		key: 'private',
		isPrivate: true,
		label: 'Private',
		hint: 'Owner and Admins only',
		icon: LuLock,
		hoverBg: 'purple.700/35',
	},
	{
		key: 'shared',
		isPrivate: false,
		label: 'Visible',
		hint: 'To anyone with access',
		icon: LuUsers,
		hoverBg: 'secondary.fixed/25',
	},
] as const;

/** Precomputed once: the overlay shown to users who can't file privately (Visible only). */
const SHARED_ONLY_ZONES = ZONES.filter((zone) => !zone.isPrivate);

/**
 * The privacy choice is the drop target: dropping a file on the left half files it privately,
 * the right half shares it with the household — no toggle, no follow-up prompt. Rendered only
 * while a file drag is in progress (the parent owns that state), as a full-screen scrim above
 * the composer.
 */
const FileDropOverlay: React.FC<FileDropOverlayProps> = (props) => {
	const { onDropFiles } = props;
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);

	// Self-gated (like ComposerPrivacyToggle): the private half exists only for users who may file
	// privately. Read here rather than as a prop so the permission check runs only while a drag is in
	// progress (when this mounts) — not on every keystroke of the parent composer. Filing privately
	// would hide the upload from its own uploader (private = Owners/Admins only), so it's not offered.
	const { canSetPrivateDocuments } = useHousePermissions();
	const { isSuperAdmin } = useAuth();
	const zones = canSetPrivateDocuments || isSuperAdmin ? ZONES : SHARED_ONLY_ZONES;

	// Portaled to the document body so the fixed full-screen scrim is never trapped by an ancestor's
	// containing block (a transform/filter/will-change on the composer dock or page shell) — that's
	// what made it stop at the content edge on some pages instead of covering the sidebar too.
	return (
		<Portal>
			<Box
				position="fixed"
				inset={0}
				zIndex={1400}
				bg="blackAlpha.700"
				css={{
					backdropFilter: 'blur(2px)',
					'@starting-style': { opacity: 0 },
					transition: `opacity 0.18s ${EASE_OUT}`,
					'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
				}}
			>
				<Flex h="100%" w="100%">
					{zones.map((zone, index) => {
						const isHovered = hoveredKey === zone.key;
						return (
							<VStack
								key={zone.key}
								flex={1}
								gap={3}
								justify="center"
								align="center"
								borderLeftWidth={index === 0 ? 0 : '1px'}
								borderColor="whiteAlpha.300"
								bg={isHovered ? zone.hoverBg : 'transparent'}
								color={isHovered ? 'white' : 'whiteAlpha.800'}
								transition={`background-color 0.12s ${EASE_OUT}, color 0.12s ${EASE_OUT}`}
								// dragenter/over must be prevented for the drop to fire; track the hovered
								// half so it can highlight as the active target.
								onDragEnter={(e: React.DragEvent) => {
									e.preventDefault();
									setHoveredKey(zone.key);
								}}
								onDragOver={(e: React.DragEvent) => {
									e.preventDefault();
									setHoveredKey(zone.key);
								}}
								onDragLeave={() => setHoveredKey((current) => (current === zone.key ? null : current))}
								onDrop={async (e: React.DragEvent) => {
									e.preventDefault();
									setHoveredKey(null);
									const files = await processDroppedItems(e.dataTransfer.items);
									if (files.length > 0) onDropFiles(files, zone.isPrivate);
								}}
							>
								<Icon as={zone.icon} boxSize={10} />
								<Title level="page" as="p">
									{zone.label}
								</Title>
								<Text textStyle="body.sm" color={isHovered ? 'whiteAlpha.900' : 'whiteAlpha.700'}>
									{zone.hint}
								</Text>
							</VStack>
						);
					})}
				</Flex>
			</Box>
		</Portal>
	);
};

export default FileDropOverlay;
